import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import type { Deca, DecaEmailRecipient, DecaEmailSending } from '@deca/api'

import { useDecaEmails } from '../app/queries.ts'
import { modifiedSinceLastEmail, recipientState, type RecipientState } from '../deca/decaEmails.ts'
import { formatDateTime } from '../deca/format.ts'
import { Button, Chip, ErrorState, LoadingRows, Notice } from './primitives.tsx'

type Tone = Parameters<typeof Chip>[0]['tone']

const TONE: Record<RecipientState, Tone> = {
  PENDING: 'neutral',
  SENT: 'info',
  DELIVERED: 'success',
  REFUSED: 'danger',
  NOT_SENT: 'neutral',
  UNKNOWN: 'neutral',
}

/**
 * An address with a line break allowed after its «@» and before each dot, so that a long one in the
 * narrow panel wraps where it reads (`bounce+…@` / `simulator.amazonses` / `.com`) rather than inside a
 * word. `overflow-wrap: anywhere` stays, for a part longer than the line on its own. A `<wbr>` adds no
 * text, so the address copies and reads as it is.
 */
function breakable(address: string) {
  return address.split(/(?<=@)|(?=\.)/).map((part, index) => (
    <Fragment key={index}>
      {index > 0 ? <wbr /> : null}
      {part}
    </Fragment>
  ))
}

/**
 * Why a letter did not arrive, or was not sent, in the words the users list already uses for its own
 * letters (#114): the bounce's reason, SES's refusal, or why an address is never written to.
 */
function whyKey(recipient: DecaEmailRecipient): string | null {
  switch (recipient.status) {
    case 'BOUNCED':
      return `admin.users.mail.reason.${recipient.reason ?? 'OTHER'}`
    case 'SKIPPED':
      return `emailTags.dead.${recipient.reason ?? 'MAILBOX_NOT_FOUND'}`
    case 'FAILED':
    case 'REJECTED':
    case 'COMPLAINED':
    case 'DELAYED':
      return `admin.users.mail.explain.${recipient.status}`
    default:
      return null
  }
}

/**
 * The receiving server's own words, verbatim, where they say why: a bounce, a refusal, a delay, a
 * complaint — as the users list shows them for an invitation (plan §3.1). Not on `FAILED` or
 * `SKIPPED`, whose `detail` is ours (an error's name, a DNS verdict), not the server's.
 */
const serverReply = (recipient: DecaEmailRecipient): boolean =>
  Boolean(recipient.detail) && ['BOUNCED', 'REJECTED', 'DELAYED', 'COMPLAINED'].includes(recipient.status ?? '')

function Sending({ sending }: { sending: DecaEmailSending }) {
  const { t, i18n } = useTranslation()
  return (
    <li className="deca-emails__sending">
      <div className="row-between small">
        <strong>{formatDateTime(sending.createdAt, i18n.language)}</strong>
        <span className="muted">
          {sending.trigger === 'AUTO' ? t('emails.auto') : t('emails.manual')} · {t('emails.by', { actor: sending.actor.display })}
        </span>
      </div>
      <ul className="deca-emails__recipients">
        {sending.recipients.map((recipient) => {
          const state = recipientState(recipient)
          const why = whyKey(recipient)
          return (
            <li key={recipient.address} className="deca-emails__recipient">
              <span className="deca-emails__address">{breakable(recipient.address)}</span>
              <Chip tone={TONE[state]}>{t(`emails.state.${state}`)}</Chip>
              {why ? <span className="small muted deca-emails__why">{t(why)}</span> : null}
              {serverReply(recipient) ? (
                <span className="small deca-emails__why">
                  {t('admin.users.mail.serverReply')}: <q>{recipient.detail}</q>
                </span>
              ) : null}
            </li>
          )
        })}
      </ul>
    </li>
  )
}

/**
 * «Envíos por correo» (#219): every sending of the document, newest first, and what became of each
 * recipient's letter — Pendiente, Enviado, Entregado or Rechazado with its reason. Above them,
 * «Modificado desde el último envío» when the document changed after it was last sent, with
 * «Enviar por correo» where the user may send it.
 */
export function DecaEmailsPanel({ deca, onSend }: { deca: Deca; onSend?: () => void }) {
  const { t } = useTranslation()
  const emails = useDecaEmails(deca.id)
  if (emails.isLoading) return <LoadingRows rows={2} columns={2} />
  if (emails.isError) return <ErrorState error={emails.error} onRetry={() => void emails.refetch()} />
  const sendings = emails.data?.data ?? []
  return (
    <div className="stack">
      {modifiedSinceLastEmail(deca) ? (
        <Notice
          tone="warning"
          actions={
            onSend ? (
              <Button icon="mail" onClick={onSend}>
                {t('detail.sendEmail')}
              </Button>
            ) : undefined
          }
        >
          {t('emails.modified')}
        </Notice>
      ) : null}
      {sendings.length === 0 ? (
        <p className="small muted">{t('emails.empty')}</p>
      ) : (
        <ul className="deca-emails">
          {sendings.map((sending) => (
            <Sending key={sending.id} sending={sending} />
          ))}
        </ul>
      )}
    </div>
  )
}
